import { toCanvas } from "html-to-image";

const SW = 1920, SH = 1080, FOOT = 46, CH = SH - FOOT, PR = 1.5;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const HIDE = ["hero-watch-video-btn"];
const TABS = {
  market: { btn: "button[data-testid^=mk-tab-]", content: () => "[data-testid=mk-tab-content]", skip: () => "overview" },
  exit: {
    btn: "button[data-testid^=exit-tab-]",
    content: (k) => `[data-testid=exit-detail-${k}]`,
    skip: () => document.querySelector("[data-testid^=exit-detail-]")?.dataset.testid.replace("exit-detail-", ""),
  },
};

const ATOMIC = (c) => ["svg", "img", "tr", "canvas", "button", "input"].includes(c.tagName.toLowerCase()) || /\b(rounded|recharts)/.test(c.getAttribute("class") || "") || /waterfall|chart/i.test(c.dataset?.testid || "") || /\bgrid\b/.test(c.parentElement?.getAttribute("class") || "");

const breakpoints = (el, origin, w, limit) => {
  const set = new Set(), lock = [];
  const walk = (node, d) => {
    for (const c of node.children) {
      const r = c.getBoundingClientRect(), t = r.top - origin, b = r.bottom - origin;
      if (r.height > 0 && ATOMIC(c) && r.height < limit) lock.push([t + 2, b - 2]);
      if (r.width >= w * 0.3 && r.height > 0) { set.add(Math.round(t)); set.add(Math.round(b)); }
      if (d < 9) walk(c, d + 1);
    }
  };
  walk(el, 0);
  return [...set].filter((y) => !lock.some(([t, b]) => y > t && y < b)).sort((x, y) => x - y);
};

const chunks = (bps, start, total, limit) => {
  const out = [];
  let y = start;
  while (y < total - 4) {
    const fits = bps.filter((b) => b > y + limit * 0.35 && b <= y + limit);
    const stretch = bps.filter((b) => b > y + limit && b <= y + limit * 1.3);
    let end = fits.length ? fits[fits.length - 1] : bps.find((b) => b > y + limit * 0.35) ?? total;
    if ((!fits.length || end - y < limit * 0.6) && stretch.length) end = stretch[stretch.length - 1];
    out.push([y, Math.min(end, total)]);
    y = end;
  }
  if (out.length > 1 && out[out.length - 1][1] - out[out.length - 1][0] < limit * 0.22) out.splice(-2, 2, [out[out.length - 2][0], total]);
  return out;
};

const capture = async (el, bg) => {
  el.scrollIntoView({ block: "start" });
  await wait(150);
  const w = el.offsetWidth, h = el.scrollHeight, origin = el.getBoundingClientRect().top;
  const canvas = await toCanvas(el, {
    pixelRatio: Math.min(PR, 15000 / h), backgroundColor: bg, cacheBust: true, width: w, height: h,
    style: { margin: "0" },
    filter: (n) => !(n.dataset && HIDE.includes(n.dataset.testid)),
  });
  const bps = breakpoints(el, origin, w, (w * (CH - 24)) / SW).filter((b) => b >= 0 && b <= h).concat([0, h]).sort((x, y) => x - y);
  return { canvas, pr: canvas.width / w, w, x0: 0, x1: w, start: 0, end: h, bps };
};

const slide = (cap, [y0, y1], bg, label, page) => {
  const c = document.createElement("canvas");
  c.width = SW * PR; c.height = SH * PR;
  const x = c.getContext("2d");
  x.scale(PR, PR);
  x.fillStyle = bg; x.fillRect(0, 0, SW, SH);
  const ch = y1 - y0, fit = (CH - 24) / ch < SW / cap.w;
  const sx = fit ? cap.x0 : 0, sw = fit ? cap.x1 - cap.x0 : cap.w;
  const s = Math.min(SW / sw, (CH - 24) / ch), dw = sw * s, dh = ch * s;
  x.imageSmoothingQuality = "high";
  x.drawImage(cap.canvas, sx * cap.pr, y0 * cap.pr, sw * cap.pr, ch * cap.pr, (SW - dw) / 2, 12, dw, dh);
  x.fillStyle = "#0F2235"; x.fillRect(0, CH, SW, FOOT);
  x.fillStyle = "#2D6E6A"; x.fillRect(0, CH, 6, FOOT);
  x.font = "500 15px 'IBM Plex Mono', monospace"; x.textBaseline = "middle";
  x.fillStyle = "rgba(255,255,255,0.85)"; x.fillText(label.toUpperCase(), 36, CH + FOOT / 2);
  x.textAlign = "right"; x.fillStyle = "rgba(255,255,255,0.5)";
  x.fillText(`MURRIETA FAMILY COMMONS · PRELIMINARY · CONFIDENTIAL   ${String(page).padStart(2, "0")}`, SW - 36, CH + FOOT / 2);
  return c.toDataURL("image/jpeg", 0.9);
};

export async function buildSlides(sections, go, onProgress) {
  const bg = getComputedStyle(document.body).backgroundColor || "#F6F4EF";
  const caps = [];
  for (const s of sections) {
    onProgress(`Rendering ${s.num} ${s.label}`);
    go(s.index);
    await wait(2200);
    const sec = document.querySelector("main [data-testid^=section-]");
    caps.push({ cap: await capture(sec, bg), label: `${s.num}  ${s.label}` });
    const t = TABS[s.id];
    if (!t) continue;
    const skip = t.skip();
    const btns = [...document.querySelectorAll(t.btn)];
    for (const b of btns) {
      const key = b.dataset.testid.replace(/^(mk|exit)-tab-/, "");
      if (key === skip) continue;
      onProgress(`Rendering ${s.num} ${s.label} · ${b.textContent.trim()}`);
      b.click();
      await wait(1600);
      const el = document.querySelector(t.content(key));
      if (el) caps.push({ cap: await capture(el, bg), label: `${s.num}  ${s.label} · ${b.textContent.trim()}` });
    }
    btns.find((b) => b.dataset.testid.endsWith(`-${skip}`))?.click();
  }
  const out = [];
  for (const { cap, label } of caps) {
    for (const ck of chunks(cap.bps, cap.start, cap.end, (cap.w * (CH - 24)) / SW)) out.push(slide(cap, ck, bg, label, out.length + 1));
  }
  return out;
}

export async function savePdf(slides, name) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [SW, SH], hotfixes: ["px_scaling"], compress: true });
  slides.forEach((d, i) => { if (i) pdf.addPage([SW, SH], "landscape"); pdf.addImage(d, "JPEG", 0, 0, SW, SH, undefined, "FAST"); });
  pdf.save(`${name}.pdf`);
}

export async function savePptx(slides, name) {
  if (!window.PptxGenJS) await new Promise((res, rej) => { const sc = document.createElement("script"); sc.src = "/vendor/pptxgen.bundle.js"; sc.onload = res; sc.onerror = rej; document.head.appendChild(sc); });
  const p = new window.PptxGenJS();
  p.layout = "LAYOUT_WIDE";
  p.title = "Murrieta Family Commons — JV Partner Presentation";
  slides.forEach((d) => p.addSlide().addImage({ data: d, x: 0, y: 0, w: 13.333, h: 7.5 }));
  await p.writeFile({ fileName: `${name}.pptx` });
}
